import { type FC, useState, useMemo } from 'react';
import {
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { IconUser } from '@tabler/icons-react';
import type { RoomTypesQuery } from '../../../gql/graphql';
import { formatPrice } from '../../../common/utilities/priceFormatter';
import { getRoomOriginalImageUrl } from '../../../common/utilities/cdnImage';
import CdnImage from '../../../components/CdnImage';
import { useAuth0 } from '../../../auth/AuthProvider';
import type { RoomDetail } from '../../../hooks/useCreateBooking';
import useValidateCoupon from '../../../hooks/useValidateCoupon';

interface Props {
  boatRoom: RoomTypesQuery | undefined;
  selectedRooms: RoomDetail[];
  currency: string;
  userId: number;
  boatId: number;
  onApplyCoupon?: (
    code: string,
    discountAmount: number,
    newTotalAmount: number,
  ) => void;
}

const RoomInfoComponent: FC<Props> = ({
  boatRoom,
  selectedRooms,
  currency,
  userId,
  boatId,
  onApplyCoupon,
}) => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [showAllRooms, setShowAllRooms] = useState(false);

  // Coupon UI state
  const [showCouponBox, setShowCouponBox] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [applied, setApplied] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  // compute initial subtotal in main currency units
  const subtotal = useMemo(() => {
    return selectedRooms.reduce((total, room) => {
      return total + room.pricePerRoom * room.numberOfRooms;
    }, 0);
  }, [selectedRooms]);

  // total after coupon
  const [discountAmount, setDiscountAmount] = useState<number | null>(null);
  const [newTotal, setNewTotal] = useState<number | null>(null);

  // coupon validation mutation
  const { mutate: validateCoupon, isPending: validating } = useValidateCoupon();

  // handle "Apply" click
  const applyCoupon = () => {
    if (!couponCode.trim()) return;
    setErrorMessage(null);
    setApplied(false);

    validateCoupon(
      {
        couponCode: couponCode.trim(),
        userId,
        totalAmount: subtotal,
        boatId,
      },
      {
        onSuccess: res => {
          if (!res.isValid) {
            setErrorMessage(res.message);
          } else {
            setDiscountAmount(res.discountAmount ?? null);
            setNewTotal(res.newTotalAmount ?? null);
            setApplied(true);
            onApplyCoupon?.(
              couponCode,
              res.discountAmount ?? 0,
              res.newTotalAmount ?? 0,
            );
          }
        },
        onError: err => {
          setErrorMessage(err.message);
        },
      },
    );
  };

  // Compute final total to display
  const finalTotal = newTotal != null ? newTotal : subtotal;

  return (
    <div className="flex p-4 flex-col items-start gap-4 rounded-lg bg-neutral-20 w-full">
      {/* Room list */}
      {selectedRooms.slice(0, showAllRooms ? undefined : 2).map(room => {
        const roomData = boatRoom?.roomTypes.find(
          r => r.typeId === room.typeId,
        );
        const perGuestPrice = room.pricePerRoom;
        return (
          <div
            key={room.typeId}
            className="flex justify-between items-center self-stretch"
          >
            <div className="flex items-center gap-4">
              <CdnImage
                className="h-20 w-20 shrink-0 object-cover"
                src={getRoomOriginalImageUrl(roomData?.roomPhotos)}
                variant="ROOMGRID"
                alt={`${roomData?.typeName}`}
              />
              <div className="flex flex-col items-start gap-2">
                <span className="font-semibold">{roomData?.typeName}</span>
                <div className="flex items-center gap-1 text-sm">
                  <IconUser size={20} />
                  <span>{roomData?.occupancy} guests</span>
                </div>
                <span className="text-sm text-neutral-500">
                  {formatPrice(perGuestPrice, { currencySymbol: currency })} per
                  guest
                </span>
              </div>
            </div>
            <span className="text-neutral-800">
              {formatPrice(perGuestPrice * room.numberOfRooms, {
                currencySymbol: currency,
              })}
            </span>
          </div>
        );
      })}

      {selectedRooms.length > 2 && (
        <button
          type="button"
          className="text-blue-neutral-800 font-semibold text-sm underline"
          onClick={() => setShowAllRooms(!showAllRooms)}
        >
          {showAllRooms ? 'See less' : 'See all rooms'}
        </button>
      )}

      <Divider className="w-full" />

      {selectedRooms.length > 2 && (
        <>
          <div className="flex justify-between items-center self-stretch">
            <span>Subtotal</span>
            <span>{formatPrice(subtotal, { currencySymbol: currency })}</span>
          </div>
          <Divider className="w-full" />
        </>
      )}

      {/* "Do you have a discount code?" */}
      <button
        type="button"
        className="text-blue-600 text-sm underline"
        onClick={() => {
          if (!isAuthenticated) {
            setLoginDialogOpen(true);
          } else {
            setShowCouponBox(true);
          }
        }}
      >
        Do you have a discount code?
      </button>

      {/* Coupon input box */}
      {showCouponBox && (
        <div className="w-full flex items-center gap-2">
          <TextField
            fullWidth
            label="Coupon code"
            value={couponCode}
            onChange={e => setCouponCode(e.target.value.toUpperCase())}
            disabled={validating || applied}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={applyCoupon}
            disabled={validating || applied}
          >
            {validating ? 'Checking…' : applied ? 'Applied' : 'Apply'}
          </Button>
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div className="text-red-600 font-medium">{errorMessage}</div>
      )}

      {/* Success banner */}
      {applied && discountAmount != null && newTotal != null && (
        <>
          <div className="flex w-full justify-between items-center p-2 bg-green-50 rounded-lg text-green-700">
            <span className="font-semibold">{couponCode} Discount</span>
            <span className="font-semibold">
              -{formatPrice(discountAmount, { currencySymbol: currency })}
            </span>
          </div>
          <Divider className="w-full" />
        </>
      )}

      {/* Final total */}
      <div className="flex justify-between items-center self-stretch">
        <span className="font-semibold">Total</span>
        <span className="font-semibold">
          {formatPrice(finalTotal, { currencySymbol: currency })}
        </span>
      </div>

      {/* Login dialog */}
      <Dialog open={loginDialogOpen} onClose={() => setLoginDialogOpen(false)}>
        <DialogTitle>Login Required</DialogTitle>
        <DialogContent>
          You must be logged in to apply a discount code. Please log in first.
          Your checkout selection is saved for this browser session.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              localStorage.setItem(
                'redirectAfterLogin',
                `${window.location.pathname}${window.location.search}`,
              );
              setLoginDialogOpen(false);
              loginWithRedirect();
            }}
          >
            Log in
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RoomInfoComponent;
